// Camera System
// Handles scrolling and viewport management

import { Vec2 } from '../utils/types';
import { clamp } from '../utils/math';

export type ScrollDirection = 'horizontal' | 'vertical' | 'both' | 'none';

export class Camera {
  // Camera position (top-left corner in world coordinates)
  private x: number = 0;
  private y: number = 0;

  // Viewport dimensions
  private viewportWidth: number;
  private viewportHeight: number;

  // Level bounds
  private levelWidth: number;
  private levelHeight: number;

  // Scrolling settings
  private scrollDirection: ScrollDirection = 'horizontal';
  private scrollSpeed: number = 0;
  private autoScroll: boolean = false;

  // Edge boundary - player cannot cross this threshold (as fraction of screen)
  private edgeZoneRatio: number = 0.33; // Player stays within middle 33% to 67%

  // Slowdown zone near level boundaries (in pixels)
  private boundarySlowdownDistance: number = 200;

  constructor(viewportWidth: number, viewportHeight: number) {
    this.viewportWidth = viewportWidth;
    this.viewportHeight = viewportHeight;
    this.levelWidth = viewportWidth;
    this.levelHeight = viewportHeight;
  }

  // Configure camera for a level
  configure(
    levelWidth: number,
    levelHeight: number,
    scrollDirection: ScrollDirection,
    scrollSpeed: number,
    autoScroll: boolean
  ): void {
    this.levelWidth = levelWidth;
    this.levelHeight = levelHeight;
    this.scrollDirection = scrollDirection;
    this.scrollSpeed = scrollSpeed;
    this.autoScroll = autoScroll;

    // Reset position
    this.x = 0;
    this.y = 0;
  }

  // Update camera position
  update(dt: number, playerPos: Vec2, _playerVelocity: Vec2): void {
    if (this.autoScroll) {
      // Auto-scroll mode - constant movement
      if (this.scrollDirection === 'horizontal') {
        this.x += this.scrollSpeed * dt;
      } else if (this.scrollDirection === 'vertical') {
        this.y += this.scrollSpeed * dt;
      }
    } else if (this.scrollDirection !== 'none') {
      // Player-driven edge scrolling - lock player at edge boundary
      this.updateEdgeLock(playerPos);
    }

    // Clamp to level bounds
    this.x = clamp(this.x, 0, Math.max(0, this.levelWidth - this.viewportWidth));
    this.y = clamp(this.y, 0, Math.max(0, this.levelHeight - this.viewportHeight));
  }

  // Edge-lock scrolling - camera moves to keep player from crossing edge boundaries
  // Smoothly slows down as camera approaches level boundaries
  private updateEdgeLock(playerPos: Vec2): void {
    // Get player's screen position
    const screenX = playerPos.x - this.x;
    const screenY = playerPos.y - this.y;

    // Calculate edge zone boundaries (where player should be stopped)
    const leftBoundary = this.viewportWidth * this.edgeZoneRatio;
    const rightBoundary = this.viewportWidth * (1 - this.edgeZoneRatio);
    const topBoundary = this.viewportHeight * this.edgeZoneRatio;
    const bottomBoundary = this.viewportHeight * (1 - this.edgeZoneRatio);

    // Calculate level boundary limits
    const minCameraX = 0;
    const maxCameraX = Math.max(0, this.levelWidth - this.viewportWidth);
    const minCameraY = 0;
    const maxCameraY = Math.max(0, this.levelHeight - this.viewportHeight);

    // Horizontal scrolling - keep player at boundary with smooth slowdown at edges
    if (this.scrollDirection === 'horizontal' || this.scrollDirection === 'both') {
      let targetX = this.x;

      if (screenX < leftBoundary) {
        // Player crossed left boundary - move camera to put them back at boundary
        targetX = playerPos.x - leftBoundary;
      } else if (screenX > rightBoundary) {
        // Player crossed right boundary - move camera to put them back at boundary
        targetX = playerPos.x - rightBoundary;
      }

      // Apply slowdown near level boundaries
      const scrollAmount = targetX - this.x;
      const slowdownFactor = this.calculateBoundarySlowdown(this.x, targetX, minCameraX, maxCameraX);
      this.x += scrollAmount * slowdownFactor;
    }

    // Vertical scrolling - keep player at boundary with smooth slowdown at edges
    if (this.scrollDirection === 'vertical' || this.scrollDirection === 'both') {
      let targetY = this.y;

      if (screenY < topBoundary) {
        // Player crossed top boundary - move camera to put them back at boundary
        targetY = playerPos.y - topBoundary;
      } else if (screenY > bottomBoundary) {
        // Player crossed bottom boundary - move camera to put them back at boundary
        targetY = playerPos.y - bottomBoundary;
      }

      // Apply slowdown near level boundaries
      const scrollAmount = targetY - this.y;
      const slowdownFactor = this.calculateBoundarySlowdown(this.y, targetY, minCameraY, maxCameraY);
      this.y += scrollAmount * slowdownFactor;
    }
  }

  // Calculate slowdown factor when approaching level boundaries
  // Returns 1.0 far from boundaries, approaches 0.0 at boundaries
  private calculateBoundarySlowdown(current: number, target: number, min: number, max: number): number {
    const direction = target - current;

    if (direction < 0) {
      // Moving toward minimum (left/top)
      const distanceToMin = current - min;
      if (distanceToMin < this.boundarySlowdownDistance) {
        return Math.max(0.05, distanceToMin / this.boundarySlowdownDistance);
      }
    } else if (direction > 0) {
      // Moving toward maximum (right/bottom)
      const distanceToMax = max - current;
      if (distanceToMax < this.boundarySlowdownDistance) {
        return Math.max(0.05, distanceToMax / this.boundarySlowdownDistance);
      }
    }

    return 1.0; // Full speed
  }

  // Set camera position immediately
  setPosition(x: number, y: number): void {
    this.x = x;
    this.y = y;
  }

  // Center camera on a position
  centerOn(worldX: number, worldY: number): void {
    this.x = worldX - this.viewportWidth / 2;
    this.y = worldY - this.viewportHeight / 2;
  }

  // Immediately center camera on position (alias for centerOn)
  centerOnImmediate(worldX: number, worldY: number): void {
    this.centerOn(worldX, worldY);
  }

  // Convert world coordinates to screen coordinates
  worldToScreen(worldPos: Vec2): Vec2 {
    return {
      x: worldPos.x - this.x,
      y: worldPos.y - this.y,
    };
  }

  // Convert screen coordinates to world coordinates
  screenToWorld(screenPos: Vec2): Vec2 {
    return {
      x: screenPos.x + this.x,
      y: screenPos.y + this.y,
    };
  }

  // Check if a world position is visible on screen
  isVisible(worldPos: Vec2, margin: number = 50): boolean {
    const screenPos = this.worldToScreen(worldPos);
    return screenPos.x >= -margin &&
           screenPos.x <= this.viewportWidth + margin &&
           screenPos.y >= -margin &&
           screenPos.y <= this.viewportHeight + margin;
  }

  // Check if a bounding box is visible
  isRectVisible(x: number, y: number, width: number, height: number, margin: number = 50): boolean {
    const screenX = x - this.x;
    const screenY = y - this.y;
    return screenX + width >= -margin &&
           screenX <= this.viewportWidth + margin &&
           screenY + height >= -margin &&
           screenY <= this.viewportHeight + margin;
  }

  // Getters
  getX(): number {
    return this.x;
  }

  getY(): number {
    return this.y;
  }

  getPosition(): Vec2 {
    return { x: this.x, y: this.y };
  }

  getViewportWidth(): number {
    return this.viewportWidth;
  }

  getViewportHeight(): number {
    return this.viewportHeight;
  }

  // Get visible world bounds
  getVisibleBounds(): { minX: number; maxX: number; minY: number; maxY: number } {
    return {
      minX: this.x,
      maxX: this.x + this.viewportWidth,
      minY: this.y,
      maxY: this.y + this.viewportHeight,
    };
  }

  // Configuration setters
  setEdgeZoneRatio(ratio: number): void {
    this.edgeZoneRatio = clamp(ratio, 0.1, 0.4);
  }
}
